import {
  claimAttachmentSummarySchema,
  type ClaimAttachmentSummary,
  type ClaimPaymentAttachmentUploadTarget,
  type AttachmentUploadTarget,
  type CompleteClaimAttachmentInput,
  type PrepareClaimAttachmentInput,
  type PrepareClaimPaymentAttachmentsInput,
  type PrepareSCIClaimAttachmentInput,
  type SCIAttachmentUploadTarget,
  type SubmitSCIClaimQueryInput,
} from '@billing/validation/attachment';
import type { ManitobaClaimQuerySubmissionResponse } from '@billing/validation/claimQuery';
import { useTranslation } from '@refinedev/core';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import queryString from 'api/utils/queryStringBuilder';
import { getRequest as get, postRequest as post } from 'api/utils/HttpMethods';
import { useListQuery, useMutationQuery } from 'api/utils/queries';

export const claimAttachmentQueryKeys = {
  all: ['claimAttachments'],
  byClaim: (claimID?: string) => ['claimAttachments', claimID ?? null],
  mutations: ['claimAttachmentMutations'],
};

export const prepareClaimAttachment = async (
  input: PrepareClaimAttachmentInput
): Promise<AttachmentUploadTarget> => {
  const result = await post<
    AttachmentUploadTarget,
    PrepareClaimAttachmentInput
  >('QBilling', '/action/claim/attachment/prepare', input);
  if (!result) throw new Error('No attachment upload target was returned.');
  return result;
};

export const completeClaimAttachment = async (
  input: CompleteClaimAttachmentInput
): Promise<void> => {
  const result = await post<
    { attachmentID: string },
    CompleteClaimAttachmentInput
  >('QBilling', '/action/claim/attachment/complete', input);
  if (result?.attachmentID !== input.attachment.id) {
    throw new Error('Attachment could not be linked to the claim.');
  }
};

const prepareClaimPaymentAttachments = async (
  input: PrepareClaimPaymentAttachmentsInput
): Promise<ClaimPaymentAttachmentUploadTarget[]> => {
  const result = await post<
    ClaimPaymentAttachmentUploadTarget[],
    PrepareClaimPaymentAttachmentsInput
  >('QBilling', '/action/claim/payment/attachments', input);

  if (!result) {
    throw new Error('No attachment upload targets were returned.');
  }

  return result;
};

export const usePrepareClaimPaymentAttachments = (): UseMutationResult<
  ClaimPaymentAttachmentUploadTarget[],
  Error,
  PrepareClaimPaymentAttachmentsInput
> => {
  const { translate } = useTranslation();

  return useMutationQuery<ClaimPaymentAttachmentUploadTarget[]>({
    mutationFn: prepareClaimPaymentAttachments,
    successMessage: '',
    errorMessage: translate(
      'pages:claims.markPaid.attachments.prepareError',
      'Unable to prepare payment attachments.'
    ),
    queryKey: claimAttachmentQueryKeys.mutations,
    displayNotification: false,
  }) as UseMutationResult<
    ClaimPaymentAttachmentUploadTarget[],
    Error,
    PrepareClaimPaymentAttachmentsInput
  >;
};

const prepareSCIClaimAttachment = async (
  input: PrepareSCIClaimAttachmentInput
): Promise<SCIAttachmentUploadTarget> => {
  const result = await post<
    SCIAttachmentUploadTarget,
    PrepareSCIClaimAttachmentInput
  >('QBilling', '/action/claim/supporting-document/prepare', input);
  if (!result)
    throw new Error('No supporting-document upload target returned.');
  return result;
};

export const usePrepareSCIClaimAttachment = (): UseMutationResult<
  SCIAttachmentUploadTarget,
  Error,
  PrepareSCIClaimAttachmentInput
> =>
  useMutationQuery<SCIAttachmentUploadTarget>({
    mutationFn: prepareSCIClaimAttachment,
    successMessage: '',
    errorMessage: '',
    queryKey: claimAttachmentQueryKeys.mutations,
    displayNotification: false,
  }) as UseMutationResult<
    SCIAttachmentUploadTarget,
    Error,
    PrepareSCIClaimAttachmentInput
  >;

const submitSCIClaimQuery = async (
  input: SubmitSCIClaimQueryInput
): Promise<ManitobaClaimQuerySubmissionResponse> =>
  post<ManitobaClaimQuerySubmissionResponse, SubmitSCIClaimQueryInput>(
    'QBilling',
    '/action/claim/supporting-document/submit',
    input
  );

export const useSubmitSCIClaimQuery = (): UseMutationResult<
  ManitobaClaimQuerySubmissionResponse,
  Error,
  SubmitSCIClaimQueryInput
> =>
  useMutationQuery<ManitobaClaimQuerySubmissionResponse>({
    mutationFn: submitSCIClaimQuery,
    successMessage: '',
    errorMessage: '',
    queryKey: claimAttachmentQueryKeys.mutations,
    displayNotification: false,
  }) as UseMutationResult<
    ManitobaClaimQuerySubmissionResponse,
    Error,
    SubmitSCIClaimQueryInput
  >;

const getClaimAttachments = async (
  claimID?: string,
  sciEligible?: boolean
): Promise<ClaimAttachmentSummary[]> => {
  if (!claimID) {
    return [];
  }

  return (
    (await get<ClaimAttachmentSummary[]>(
      'QBilling',
      `/resource/claim/attachments${queryString({ claimID, sciEligible })}`
    )) ?? []
  );
};

export const useGetClaimAttachments = ({
  claimID,
  enabled,
  sciEligible,
}: {
  claimID?: string;
  enabled: boolean;
  sciEligible?: boolean;
}): UseQueryResult<ClaimAttachmentSummary[], Error> =>
  useListQuery<ClaimAttachmentSummary>({
    queryKey: [...claimAttachmentQueryKeys.byClaim(claimID), { sciEligible }],
    fetchFunction: () => getClaimAttachments(claimID, sciEligible),
    functionName: 'getClaimAttachments',
    isEntity: (value): value is ClaimAttachmentSummary =>
      claimAttachmentSummarySchema.safeParse(value).success,
    params: { claimID, sciEligible },
    enabled: enabled && Boolean(claimID),
    displayNotification: false,
  });

export const getAttachmentDownloadURL = async (
  attachmentID: string
): Promise<string> => {
  const result = await get<{ downloadURL: string }>(
    'QBilling',
    `/action/attachment/download${queryString({ attachmentID })}`
  );
  if (!result?.downloadURL) {
    throw new Error('Attachment download URL could not be generated.');
  }
  return result.downloadURL;
};
